import {
  useAgent,
  useCopilotKit,
  useRenderToolCall,
  useSuggestions,
} from "@copilotkit/react-core/v2";
import {Microphone, Paperclip} from "@gravity-ui/icons";
import {
  ChatAttachment,
  ChatAttachmentGroup,
  ChatAttachmentInput,
  ChatConversation,
  Chip,
  FloatingToc,
  PromptInput,
  useMediaQuery,
} from "@project/design-system-web";
import {aiStore, useAIStore} from "@project/extensions/ai-assistant";
import {useI18n} from "@project/i18n/react";
import {SendIcon, StopCircleIcon} from "@project/icons-web";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";

import {
  getMessageContentText,
  getMessageRole,
  getMessageToolCalls,
  getToolCallId,
  isRenderableCopilotMessage,
} from "../adapters/copilot-message-adapter";
import {aiAssistantMessages} from "../ai-assistant.messages";
import {ClientToolsRegistry} from "../integration/client-tools-registry";
import {GenerativeUIRegistry} from "../integration/generative-ui-registry";
import {HITLRegistry} from "../integration/hitl-registry";
import {createClientId} from "../utils/create-client-id";

import {AssistantBottomAffordance} from "./assistant-bottom-affordance";
import {AssistantMessageList} from "./assistant-message-list";
import {buildUserMessageContent, useAssistantAttachments} from "./use-assistant-attachments";
import {useAssistantConversationScroll} from "./use-assistant-conversation-scroll";

const CHAT_CONTENT_MAX_WIDTH = 714;
const TOC_MIN_GUTTER_WIDTH = 96;
const TOC_LABEL_MAX_LENGTH = 56;

type AgentMessage = ReturnType<typeof useAgent>["agent"]["messages"][number];

function getPromptTextArea(container: HTMLDivElement | null): HTMLTextAreaElement | null {
  return container?.querySelector("textarea") ?? null;
}

function getTocLabel(index: number, text: string, attachmentMessageLabel: string): string {
  const firstLine = text.split("\n")[0]?.trim() || attachmentMessageLabel;
  const truncated =
    firstLine.length > TOC_LABEL_MAX_LENGTH
      ? `${firstLine.slice(0, TOC_LABEL_MAX_LENGTH - 1)}…`
      : firstLine;

  return `#${index} ${truncated}`;
}

function isAssistantTocEnabled(): boolean {
  return true;
}

function updateMessageContentPreservingAttachments(message: AgentMessage, nextText: string) {
  if (typeof message.content === "string") {
    return nextText;
  }

  if (!Array.isArray(message.content)) {
    return nextText;
  }

  const trimmedText = nextText.trim();
  const nextParts = [...message.content];
  const textPartIndex = nextParts.findIndex(
    (part) =>
      part &&
      typeof part === "object" &&
      "type" in part &&
      part.type === "text" &&
      "text" in part &&
      typeof part.text === "string",
  );

  if (trimmedText) {
    if (textPartIndex >= 0) {
      nextParts[textPartIndex] = {
        ...(nextParts[textPartIndex] as Record<string, unknown>),
        text: trimmedText,
        type: "text",
      };
    } else {
      nextParts.unshift({text: trimmedText, type: "text"});
    }
  } else if (textPartIndex >= 0) {
    nextParts.splice(textPartIndex, 1);
  }

  return nextParts;
}

export function AISidebar() {
  const {agent} = useAgent();
  const {copilotkit} = useCopilotKit();
  const renderToolCall = useRenderToolCall();
  const {suggestions} = useSuggestions();
  const {t} = useI18n();
  const [inputValue, setInputValue] = useState("");
  const [activeTocMessageId, setActiveTocMessageId] = useState<string | undefined>();
  const [editingMessageId, setEditingMessageId] = useState<string | undefined>();
  const promptContainerRef = useRef<HTMLDivElement>(null);
  const retainFocusRef = useRef(true);
  const previousBlockerCountRef = useRef(0);
  const userMessageRefs = useRef(new Map<string, HTMLDivElement>());
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const {attachments, clearAttachments, handleFilesSelected, removeAttachment, uploadAttachments} =
    useAssistantAttachments();

  const namespaces = useAIStore((state) => state.namespaces);
  const conversationBlockers = useAIStore((state) => state.conversationBlockers);
  const blockerCount = Object.keys(conversationBlockers).length;
  const hasConversationBlocker = blockerCount > 0;
  const renderableMessages = useMemo(
    () => agent.messages.filter(isRenderableCopilotMessage),
    [agent.messages],
  );
  const attachmentMessageLabel = t(aiAssistantMessages.attachmentMessage);
  const userTocItems = useMemo(() => {
    let userMessageIndex = 0;

    return renderableMessages
      .filter((message) => getMessageRole(message) === "user")
      .map((message) => {
        userMessageIndex += 1;

        return {
          id: String(message.id),
          label: getTocLabel(
            userMessageIndex,
            getMessageContentText(message),
            attachmentMessageLabel,
          ),
        };
      });
  }, [attachmentMessageLabel, renderableMessages]);
  const userMessageNumbers = useMemo(() => {
    return new Map(userTocItems.map((item, index) => [item.id, index + 1]));
  }, [userTocItems]);
  const focusPrompt = useCallback(() => {
    window.requestAnimationFrame(() => getPromptTextArea(promptContainerRef.current)?.focus());
  }, []);

  const {
    conversationContentRef,
    conversationRef,
    conversationWidth,
    handleConversationScroll,
    isDetachedFromBottom,
    markUserScrollIntent,
    scrollState,
    scrollToBottomAndFollow,
  } = useAssistantConversationScroll({
    contentResizeKey: `${agent.messages.length}:${renderableMessages.length}`,
  });

  const shouldShowToc =
    isAssistantTocEnabled() &&
    isDesktop &&
    userTocItems.length >= 5 &&
    conversationWidth >= CHAT_CONTENT_MAX_WIDTH + TOC_MIN_GUTTER_WIDTH;

  const updateActiveTocItem = useCallback(() => {
    const element = conversationRef.current;

    if (!element || userTocItems.length === 0) return;

    const activationLine = element.scrollTop + 96;
    let nextActiveId = userTocItems[0]?.id;

    for (const item of userTocItems) {
      const node = userMessageRefs.current.get(item.id);

      if (node && node.offsetTop <= activationLine) nextActiveId = item.id;
    }

    setActiveTocMessageId((current) => (current === nextActiveId ? current : nextActiveId));
  }, [conversationRef, userTocItems]);

  useEffect(() => {
    retainFocusRef.current = true;
    focusPrompt();
  }, [focusPrompt]);

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null;

      if (!target) return;
      if (promptContainerRef.current?.contains(target)) return;
      if (target.closest('[role="dialog"]')) return;

      retainFocusRef.current = false;
    };

    document.addEventListener("pointerdown", handlePointerDown, true);

    return () => document.removeEventListener("pointerdown", handlePointerDown, true);
  }, []);

  useEffect(() => {
    const previousBlockerCount = previousBlockerCountRef.current;

    previousBlockerCountRef.current = blockerCount;

    if (previousBlockerCount > 0 && blockerCount === 0 && retainFocusRef.current) {
      focusPrompt();
    }
  }, [blockerCount, focusPrompt]);

  useEffect(() => {
    if (!activeTocMessageId && userTocItems[0]) setActiveTocMessageId(userTocItems[0].id);
  }, [activeTocMessageId, userTocItems]);

  useEffect(() => {
    // Capture context for new tool calls
    const toolCalls = agent.messages.flatMap(getMessageToolCalls);

    for (const tc of toolCalls) {
      const toolCallId = getToolCallId(tc);

      if (toolCallId && !aiStore.state.toolCallContexts[toolCallId]) {
        // Find the active user context from registered namespaces
        const nsWithUser = Object.values(namespaces).find(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (ns) => (ns.context?.currentUser as any)?.id,
        );

        if (nsWithUser) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const userId = (nsWithUser.context?.currentUser as any)?.id;

          if (typeof userId === "string" && userId) {
            aiStore.state.setToolCallContext(toolCallId, userId);
          }
        }
      }
    }
  }, [agent.messages, namespaces]);

  const cancelEditing = useCallback(() => {
    setEditingMessageId(undefined);
  }, []);

  const setAgentMessages = useCallback(
    (messages: AgentMessage[]) => {
      agent.setMessages(messages as never);
    },
    [agent],
  );

  const handleRetryAssistantMessage = useCallback(
    async (message: AgentMessage) => {
      if (agent.isRunning) return;

      const messageIndex = agent.messages.findIndex((entry) => entry.id === message.id);

      if (messageIndex <= 0) return;

      retainFocusRef.current = true;
      cancelEditing();
      setAgentMessages(agent.messages.slice(0, messageIndex));
      scrollToBottomAndFollow("auto", "retry-assistant-message");
      focusPrompt();
      await copilotkit.runAgent({agent});
      if (retainFocusRef.current) focusPrompt();
    },
    [agent, cancelEditing, copilotkit, focusPrompt, scrollToBottomAndFollow, setAgentMessages],
  );

  const handleEditUserMessage = useCallback(
    (message: AgentMessage) => {
      if (agent.isRunning) return;

      setEditingMessageId(String(message.id));
      retainFocusRef.current = false;
      scrollToBottomAndFollow("smooth", "edit-user-message");
    },
    [agent.isRunning, scrollToBottomAndFollow],
  );

  const handleSubmitUserMessageEdit = useCallback(
    async (message: AgentMessage, nextText: string) => {
      if (agent.isRunning) return;

      const messageIndex = agent.messages.findIndex((entry) => entry.id === message.id);
      const messageToEdit = messageIndex >= 0 ? agent.messages[messageIndex] : undefined;

      if (!messageToEdit || getMessageRole(messageToEdit) !== "user") {
        cancelEditing();

        return;
      }

      retainFocusRef.current = true;

      const updatedMessage = {
        ...messageToEdit,
        content: updateMessageContentPreservingAttachments(messageToEdit, nextText),
      };

      setAgentMessages([...agent.messages.slice(0, messageIndex), updatedMessage as AgentMessage]);
      setEditingMessageId(undefined);
      scrollToBottomAndFollow("auto", "handleEditResubmit");
      focusPrompt();
      await copilotkit.runAgent({agent});
      if (retainFocusRef.current) focusPrompt();
    },
    [agent, cancelEditing, copilotkit, focusPrompt, scrollToBottomAndFollow, setAgentMessages],
  );

  const handleSend = async (text: string) => {
    if (agent.isRunning) return;
    if (!text.trim() && attachments.length === 0) return;

    retainFocusRef.current = true;

    try {
      const uploadedAttachments = await uploadAttachments();

      agent.addMessage({
        id: createClientId("message"),
        role: "user",
        content: buildUserMessageContent(text, uploadedAttachments),
      });
      setInputValue("");
      clearAttachments();
      scrollToBottomAndFollow("auto", "handleSend");
      focusPrompt();
      await copilotkit.runAgent({agent});
      if (retainFocusRef.current) focusPrompt();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to send message with attachments", error);
    }
  };

  const handleStop = () => {
    copilotkit.stopAgent({agent});
  };

  const handleBottomAffordancePress = () => {
    scrollToBottomAndFollow(agent.isRunning ? "auto" : "smooth", "bottom-affordance");
    retainFocusRef.current = true;
    focusPrompt();
  };

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      <GenerativeUIRegistry />
      <ClientToolsRegistry />
      <HITLRegistry />

      <div className="relative min-h-0 flex-1">
        <ChatConversation
          ref={conversationRef}
          className="h-full"
          initial="instant"
          resize="instant"
          onKeyDown={(event) => {
            if (["ArrowDown", "ArrowUp", "End", "Home", "PageDown", "PageUp"].includes(event.key)) {
              markUserScrollIntent("key");
            }
          }}
          onScroll={() => {
            handleConversationScroll();
            updateActiveTocItem();
          }}
          onTouchMove={() => markUserScrollIntent("touch")}
          onWheel={() => markUserScrollIntent("wheel")}
        >
          <div ref={conversationContentRef}>
            <AssistantMessageList
              agentIsRunning={agent.isRunning}
              allMessages={agent.messages}
              editingMessageId={editingMessageId}
              messages={renderableMessages}
              onCancelUserMessageEdit={cancelEditing}
              onRetryAssistantMessage={(message) => {
                void handleRetryAssistantMessage(message);
              }}
              onStartUserMessageEdit={handleEditUserMessage}
              onSubmitUserMessageEdit={handleSubmitUserMessageEdit}
              renderToolCall={renderToolCall}
              userMessageNumbers={userMessageNumbers}
              userMessageRefs={userMessageRefs}
            />
          </div>

          <AssistantBottomAffordance
            hasConversationBlocker={hasConversationBlocker}
            isDetachedFromBottom={isDetachedFromBottom}
            isRunning={agent.isRunning}
            onPress={handleBottomAffordancePress}
            scrollState={scrollState}
          />
        </ChatConversation>

        {shouldShowToc && (
          <div className="absolute top-1/2 right-3 z-10 -translate-y-1/2">
            <FloatingToc triggerMode="press">
              <FloatingToc.Trigger aria-label={t(aiAssistantMessages.conversationNavigation)}>
                {userTocItems.map((item) => (
                  <FloatingToc.Bar key={item.id} active={item.id === activeTocMessageId} />
                ))}
              </FloatingToc.Trigger>
              <FloatingToc.Content>
                <span className="text-muted mb-1 block px-3 py-1 text-[10px] font-semibold tracking-wider uppercase">
                  {t(aiAssistantMessages.messages)}
                </span>
                {userTocItems.map((item) => (
                  <FloatingToc.Item
                    key={item.id}
                    active={item.id === activeTocMessageId}
                    onClick={() => {
                      userMessageRefs.current.get(item.id)?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                      setActiveTocMessageId(item.id);
                    }}
                  >
                    {item.label}
                  </FloatingToc.Item>
                ))}
              </FloatingToc.Content>
            </FloatingToc>
          </div>
        )}
      </div>

      {/* Suggestions */}
      {suggestions && suggestions.length > 0 && (
        <div className="flex flex-wrap gap-2 p-4 pb-0">
          {suggestions.map((s, i) => (
            <Chip
              key={i}
              size="sm"
              variant="secondary"
              className="cursor-pointer"
              onClick={() => handleSend(s.message)}
            >
              {s.title}
            </Chip>
          ))}
        </div>
      )}

      {/* Input Area */}
      <div ref={promptContainerRef} className="p-4">
        <PromptInput
          variant="primary"
          layout="inline"
          lockInputOnRun={false}
          status={agent.isRunning ? "streaming" : "ready"}
          value={inputValue}
          onStop={handleStop}
          onSubmit={() => {
            void handleSend(inputValue);
          }}
          onValueChange={setInputValue}
        >
          <ChatAttachmentInput onFilesSelected={handleFilesSelected}>
            <ChatAttachmentInput.Dropzone
              render={(dropzoneProps) => (
                <PromptInput.Shell {...dropzoneProps}>
                  <PromptInput.Content>
                    {attachments.length ? (
                      <PromptInput.Attachments>
                        <ChatAttachmentGroup>
                          {attachments.map((attachment) => (
                            <ChatAttachment
                              key={attachment.id}
                              mimeType={attachment.mimeType}
                              name={attachment.name}
                              src={attachment.src}
                            >
                              <ChatAttachment.Preview />
                              <ChatAttachment.Remove
                                aria-label={t(aiAssistantMessages.removeAttachment)}
                                onPress={() => removeAttachment(attachment.id)}
                              />
                            </ChatAttachment>
                          ))}
                        </ChatAttachmentGroup>
                      </PromptInput.Attachments>
                    ) : null}
                    <PromptInput.TextArea placeholder={t(aiAssistantMessages.promptPlaceholder)} />
                  </PromptInput.Content>
                  <PromptInput.Toolbar>
                    <PromptInput.ToolbarStart>
                      <ChatAttachmentInput.Trigger
                        render={(triggerProps) => (
                          <PromptInput.Action
                            {...triggerProps}
                            aria-label={t(aiAssistantMessages.attachFile)}
                            tooltip={t(aiAssistantMessages.attachFile)}
                          >
                            <Paperclip className="size-4" />
                          </PromptInput.Action>
                        )}
                      />
                    </PromptInput.ToolbarStart>
                    <PromptInput.ToolbarEnd>
                      <PromptInput.Action
                        aria-label={t(aiAssistantMessages.useVoice)}
                        tooltip={t(aiAssistantMessages.useVoice)}
                        isDisabled={Boolean(agent.isRunning || inputValue.trim())}
                      >
                        <Microphone className="size-4" />
                      </PromptInput.Action>
                      <PromptInput.Send
                        className={agent.isRunning ? "bg-danger/80" : undefined}
                        aria-label={
                          agent.isRunning
                            ? t(aiAssistantMessages.stopGeneration)
                            : t(aiAssistantMessages.sendMessage)
                        }
                        isDisabled={
                          !agent.isRunning && !inputValue.trim() && attachments.length === 0
                        }
                        onStop={handleStop}
                      >
                        {agent.isRunning ? (
                          <StopCircleIcon className="size-4" />
                        ) : (
                          <SendIcon className="size-4" />
                        )}
                      </PromptInput.Send>
                    </PromptInput.ToolbarEnd>
                  </PromptInput.Toolbar>
                </PromptInput.Shell>
              )}
            />
          </ChatAttachmentInput>
          <PromptInput.Footer>{t(aiAssistantMessages.footerDisclaimer)}</PromptInput.Footer>
        </PromptInput>
      </div>
    </div>
  );
}
